/**
 * DAGView — React Flow graph for provenance visualisation.
 *
 * Uses @xyflow/react with ELK layout to render the provenance DAG.
 * Supports collapsing parameter-sweep runs into a single group node.
 *
 * Selection is managed in ProvenanceContext so clicking a node here
 * propagates to Data Explorer rows and Changelog events.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Panel,
  useNodesState,
  useEdgesState,
  useReactFlow,
  useNodesInitialized,
  type Node,
  type Edge,
  type Viewport,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import "./dag-transitions.css";

import { Button } from "@radix-ui/themes";
import { useTheme } from "../../context/ThemeContext";
import { useProvenanceContext } from "../../context/ProvenanceContext";
import { usePlotData } from "../../context/PlotDataContext";
import { useViewState, type ViewStateSlice } from "../../context/ViewStateContext";
import { useProvenance, useDeleteNode } from "../../hooks/useProvenance";
import { useRunTable } from "../../hooks/useRunTable";
import type { DAGNode as ProvenanceNode } from "../../api/artifacts";
import { layoutDAG } from "./dagLayout";
import { detectSweepGroups, applySweepGroups, detectRefQuantityGroups, applyRefQuantityGroups, injectAllDataNode, detectSourceGroups, applySourceGroups } from "./dagGrouping";
import SweepGroupNode from "./SweepGroupNode";
import RefQuantityGroupNode from "./RefQuantityGroupNode";
import SourceGroupNode from "./SourceGroupNode";
import AllDataNode from "./AllDataNode";
import { DetailPillNode } from "./DetailPillNode";
import { PlotNode } from "./PlotNode";
import { CommentBadgeNode } from "./CommentBadgeNode";
import { injectDetailNodes } from "./dagDetails";
import { MeshActionPanel } from "./MeshActionPanel";
import { RefQuantityEditModal } from "./RefQuantityEditModal";
import { useCommentCounts } from "../../hooks/useComments";
import { ProvenancePopover, useDataPointContextMenu, DataPointContextMenu } from "../shared/DataPointContextMenu";
import { ElkRoutedEdge } from "./ElkRoutedEdge";
import { edgeTooltipContent } from "./dagTooltips";
import { DAG_AUTO_FIT_OPTIONS, clampDagViewport } from "./dagViewport";
import { ZoomBridge } from "./ZoomBridge";
import { detectWingArea, confirmWingArea, type WingAreaDetectionResult } from "../../flow360/api";
import { setReferenceQuantity } from "../../api/projects";
import { usePlanformStore } from "../../stores/planformStore";
import { COMMENT_BADGE_HEIGHT, COMMENT_BADGE_WIDTH } from "./dagNodeSizing";
import { useAnonymous } from "../../context/AnonymousContext";

/** BFS from nodeId; returns IDs of all descendant nodes with kind === "run". */
function getDescendantRunIds(
  nodeId: string,
  parentToChildren: Map<string, string[]>,
  nodesById: Map<string, ProvenanceNode>,
): string[] {
  const runIds: string[] = [];
  const visited = new Set<string>();
  const queue: string[] = [nodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (visited.has(current)) continue;
    visited.add(current);
    const children = parentToChildren.get(current);
    if (children) {
      for (const c of children) if (!visited.has(c)) queue.push(c);
    }
    if (current !== nodeId) {
      const node = nodesById.get(current);
      if (node?.kind === "run") runIds.push(current);
    }
  }
  return runIds;
}

const KIND_COLORS: Record<string, string> = {
  geometry: "#3b82f6",
  surface_mesh: "#8b5cf6",
  volume_mesh: "#06b6d4",
  run: "#22c55e",
  saved_plot: "#f59e0b",
  reference_quantity: "#f97316",
  analysis: "#ec4899",
  all_data: "#2563eb",
  dataframe: "#3b82f6",
  series: "#3b82f6",
  parameter: "#22c55e",
  result: "#22c55e",
  coefficient: "#22c55e",
  scalar: "#a855f7",
  cache_entry: "#64748b",
  "export/csv": "#3b82f6",
  "export/png": "#f59e0b",
};

function desaturateColor(hex: string, ratio = 0.5): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const gray = Math.round(r * 0.299 + g * 0.587 + b * 0.114);
  const blend = (ch: number) => Math.round(ch + (gray - ch) * ratio);
  const h = (n: number) => blend(n).toString(16).padStart(2, "0");
  return `#${h(r)}${h(g)}${h(b)}`;
}

const MESH_KINDS = new Set(["surface_mesh", "volume_mesh"]);

/**
 * Hard cap on sweep-group expansion. ELK layout degrades badly past a few
 * hundred sibling nodes (2300-run Maxwell-57 project froze the browser),
 * and the user can't productively interact with nodes at that density.
 * Collapsing is always allowed — only expansion is blocked.
 */
const MAX_EXPANDABLE_SWEEP_SIZE = 200;

const PRO_OPTIONS = { hideAttribution: true };

const NODE_TYPES = {
  sweepGroup: SweepGroupNode,
  refQuantityGroup: RefQuantityGroupNode,
  sourceGroup: SourceGroupNode,
  allDataNode: AllDataNode,
  detailPill: DetailPillNode,
  plotNode: PlotNode,
  commentBadge: CommentBadgeNode,
};

const EDGE_TYPES = {
  elkRouted: ElkRoutedEdge,
};

/**
 * Re-fits the viewport every time `layoutId` changes (i.e. after each ELK
 * layout). The 50ms delay lets CSS transform transitions begin before the
 * camera pans, so nodes glide and the viewport follows smoothly.
 */
function LayoutFitter({ layoutId }: { layoutId: number }) {
  const { fitView } = useReactFlow();

  useEffect(() => {
    if (layoutId === 0) return;
    const timer = setTimeout(() => fitView(DAG_AUTO_FIT_OPTIONS), 50);
    return () => clearTimeout(timer);
  }, [layoutId, fitView]);

  return null;
}

/**
 * Keeps the viewport center stable when the container width changes
 * (e.g. sidebar opening/closing). Without this the DAG appears to
 * slide sideways as the panel animates.
 */
function ViewportStabilizer({ containerRef }: { containerRef: React.RefObject<HTMLDivElement | null> }) {
  const { getViewport, setViewport } = useReactFlow();
  const prevWidth = useRef<number | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    prevWidth.current = el.clientWidth;

    let resizeTimer: ReturnType<typeof setTimeout>;
    const observer = new ResizeObserver((entries) => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        for (const entry of entries) {
          const newWidth = entry.contentRect.width;
          const oldWidth = prevWidth.current;
          if (oldWidth !== null && Math.abs(newWidth - oldWidth) > 2) {
            const vp = getViewport();
            setViewport(clampDagViewport({ x: vp.x + (newWidth - oldWidth) / 2, y: vp.y, zoom: vp.zoom }));
          }
          prevWidth.current = newWidth;
        }
      }, 100);
    });

    observer.observe(el);
    return () => { clearTimeout(resizeTimer); observer.disconnect(); };
  }, [containerRef, getViewport, setViewport]);

  return null;
}

/** Registers DAG viewport + toggle state with ViewStateContext for sharing. */
function DAGViewStateRegistrar({
  groupingEnabled, expandedGroups, expandedDetails, detailsGloballyVisible,
  setGroupingEnabled, setExpandedGroups, setExpandedDetails, setDetailsGloballyVisible,
}: {
  groupingEnabled: boolean;
  expandedGroups: Set<string>;
  expandedDetails: Set<string>;
  detailsGloballyVisible: boolean;
  setGroupingEnabled: (v: boolean) => void;
  setExpandedGroups: (v: Set<string>) => void;
  setExpandedDetails: (v: Set<string>) => void;
  setDetailsGloballyVisible: (v: boolean) => void;
}) {
  const { getViewport, setViewport } = useReactFlow();
  const { register } = useViewState();

  const refs = useRef({ groupingEnabled, expandedGroups, expandedDetails, detailsGloballyVisible });
  refs.current = { groupingEnabled, expandedGroups, expandedDetails, detailsGloballyVisible };

  useEffect(() => register(
    "dag",
    () => ({
      viewport: getViewport(),
      groupingEnabled: refs.current.groupingEnabled,
      expandedGroups: [...refs.current.expandedGroups],
      expandedDetails: [...refs.current.expandedDetails],
      detailsGloballyVisible: refs.current.detailsGloballyVisible,
    }),
    (state: ViewStateSlice) => {
      if (state.viewport) {
        const v = state.viewport as { x: number; y: number; zoom: number };
        setViewport(clampDagViewport(v), { duration: 300 });
      }
      if (state.groupingEnabled !== undefined) setGroupingEnabled(state.groupingEnabled as boolean);
      if (state.expandedGroups) setExpandedGroups(new Set(state.expandedGroups as string[]));
      if (state.expandedDetails) setExpandedDetails(new Set(state.expandedDetails as string[]));
      if (state.detailsGloballyVisible !== undefined) setDetailsGloballyVisible(state.detailsGloballyVisible as boolean);
    },
  ), [register, getViewport, setViewport, setGroupingEnabled, setExpandedGroups, setExpandedDetails, setDetailsGloballyVisible]);

  return null;
}

interface Props {
  projectId: string;
}

export function DAGView({ projectId }: Props) {
  const { theme } = useTheme();
  const isLight = theme === "light";
  const { isAnonymous } = useAnonymous();
  const { currentBranch, selectedNodeIds, selectNodes, clearSelection, selectArtifact, selectedArtifactId, setOpenPanel } = useProvenanceContext();
  const [, setSearchParams] = useSearchParams();
  const { data, isLoading, isError } = useProvenance(projectId, currentBranch, { scope: "runs" });
  const { data: runTableData } = useRunTable(projectId, currentBranch);
  const deleteMutation = useDeleteNode(projectId, currentBranch);
  const nodeIds = useMemo(() => data?.nodes.map((n) => n.id), [data?.nodes]);
  const { data: commentCounts } = useCommentCounts(projectId, currentBranch, nodeIds);
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const layoutIdRef = useRef(0);
  const [layoutId, setLayoutId] = useState(0);
  const dagContainerRef = useRef<HTMLDivElement>(null);

  const [viewport, setViewportState] = useState<Viewport>({ x: 0, y: 0, zoom: 1 });
  const onViewportChange = useCallback((vp: Viewport) => {
    setViewportState({ x: Math.round(vp.x), y: Math.round(vp.y), zoom: vp.zoom });
  }, []);

  const [groupingEnabled, setGroupingEnabled] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [expandedRefGroups, setExpandedRefGroups] = useState<Set<string>>(new Set());
  const [selectedMeshNode, setSelectedMeshNode] = useState<ProvenanceNode | null>(null);
  const [editingQuantity, setEditingQuantity] = useState<{ node: ProvenanceNode; geometryId: string | null } | null>(null);
  const [autoDetectState, setAutoDetectState] = useState<{
    detecting: boolean;
    result: WingAreaDetectionResult | null;
    error: string | null;
    geometryId: string;
  } | null>(null);
  const [expandedDetails, setExpandedDetails] = useState<Set<string>>(new Set());
  const [detailsGloballyVisible, setDetailsGloballyVisible] = useState(false);
  const nodesInitialized = useNodesInitialized();
  const pendingLayoutStabilizationRef = useRef<number | null>(null);
  const prevTopologySignatureRef = useRef<string>("");
  const [showFilteredDAG, setShowFilteredDAG] = useState(false);
  const [edgeHover, setEdgeHover] = useState<{ x: number; y: number; text: string } | null>(null);

  const { fullData, filteredData } = usePlotData();
  const hasActiveFilter = filteredData.length > 0 && filteredData.length < fullData.length;
  const paneCtxMenu = useDataPointContextMenu(fullData);

  // Pre-compute adjacency + lookup maps once per data change. O(N+E) build,
  // O(1) lookups from context menus / descendant walks / filtered-view build.
  const dataMaps = useMemo(() => {
    const nodesById = new Map<string, ProvenanceNode>();
    const parentToChildren = new Map<string, string[]>();
    const childToParents = new Map<string, string[]>();
    if (data) {
      for (const n of data.nodes) nodesById.set(n.id, n);
      for (const e of data.edges) {
        const cs = parentToChildren.get(e.parent_id);
        if (cs) cs.push(e.child_id);
        else parentToChildren.set(e.parent_id, [e.child_id]);
        const ps = childToParents.get(e.child_id);
        if (ps) ps.push(e.parent_id);
        else childToParents.set(e.child_id, [e.parent_id]);
      }
    }
    return { nodesById, parentToChildren, childToParents };
  }, [data]);

  const effectiveData = useMemo(() => {
    if (!data || !showFilteredDAG || !hasActiveFilter) return data;

    const filteredRunIds = new Set(
      filteredData.map(row => row._node_id as string).filter(Boolean),
    );

    const visibleIds = new Set<string>();
    for (const node of data.nodes) {
      if (node.kind === "run" && filteredRunIds.has(node.id)) visibleIds.add(node.id);
    }

    // Walk parents backwards to include ancestors (geometry, meshes).
    const queue = [...visibleIds];
    while (queue.length > 0) {
      const id = queue.shift()!;
      for (const parent of (dataMaps.childToParents.get(id) ?? [])) {
        if (!visibleIds.has(parent)) {
          visibleIds.add(parent);
          queue.push(parent);
        }
      }
    }

    // Include saved_plot children of visible nodes.
    for (const pid of [...visibleIds]) {
      for (const child of (dataMaps.parentToChildren.get(pid) ?? [])) {
        const node = dataMaps.nodesById.get(child);
        if (node?.kind === "saved_plot") visibleIds.add(child);
      }
    }

    return {
      nodes: data.nodes.filter(n => visibleIds.has(n.id)),
      edges: data.edges.filter(e => visibleIds.has(e.parent_id) && visibleIds.has(e.child_id)),
    };
  }, [data, showFilteredDAG, hasActiveFilter, filteredData, dataMaps]);

  const effectiveMaps = useMemo(() => {
    if (!showFilteredDAG || !hasActiveFilter) return dataMaps;
    const nodesById = new Map<string, ProvenanceNode>();
    const parentToChildren = new Map<string, string[]>();
    const childToParents = new Map<string, string[]>();
    if (effectiveData) {
      for (const n of effectiveData.nodes) nodesById.set(n.id, n);
      for (const e of effectiveData.edges) {
        const cs = parentToChildren.get(e.parent_id);
        if (cs) cs.push(e.child_id);
        else parentToChildren.set(e.parent_id, [e.child_id]);
        const ps = childToParents.get(e.child_id);
        if (ps) ps.push(e.parent_id);
        else childToParents.set(e.child_id, [e.parent_id]);
      }
    }
    return { nodesById, parentToChildren, childToParents };
  }, [effectiveData, showFilteredDAG, hasActiveFilter, dataMaps]);

  const topologySignature = useMemo(() => {
    // Cheap signature: count + first/last ids. Avoids sort + join of 9000
    // uuids on every render. Mis-ordering without count change would be
    // a no-op for ELK (same node set) so not a correctness risk.
    const ns = effectiveData?.nodes ?? [];
    const first = ns[0]?.id ?? "";
    const last = ns[ns.length - 1]?.id ?? "";
    return `${ns.length}|${first}|${last}`;
  }, [effectiveData?.nodes]);


  const selectArtifactRef = useRef(selectArtifact);
  selectArtifactRef.current = selectArtifact;

  const runTableDataRef = useRef(runTableData);
  runTableDataRef.current = runTableData;

  const selectedArtifactIdRef = useRef(selectedArtifactId);
  selectedArtifactIdRef.current = selectedArtifactId;

  const commentCountsRef = useRef(commentCounts);
  commentCountsRef.current = commentCounts;

  const [ctxMenu, setCtxMenu] = useState<{
    x: number; y: number;
    nodeId?: string; nodeName?: string;
    edgeId?: string; edgeLabel?: string;
  } | null>(null);
  const edgeTooltipNodes = useMemo(
    () =>
      nodes.flatMap((node) => {
        if (node.type === "sweepGroup") {
          const label = typeof node.data?.label === "string" ? node.data.label : node.id;
          return [{ id: node.id, kind: "sweep_group", name: label }];
        }
        if (node.type === "allDataNode") {
          return [{ id: node.id, kind: "all_data", name: "All Data" }];
        }
        const dagNode = node.data?.dagNode as ProvenanceNode | undefined;
        if (!dagNode) return [];
        const raw = dagNode as Record<string, unknown>;
        return [{
          id: dagNode.id,
          kind: dagNode.kind,
          name: dagNode.name,
          created_by: raw.created_by as string | undefined,
          created_at: raw.created_at as string | undefined,
        }];
      }),
    [nodes],
  );

  const toggleNodeDetails = useCallback((nodeId: string) => {
    setExpandedDetails((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  const handleAllDataClick = useCallback(() => {
    const runIds = (effectiveData?.nodes ?? [])
      .filter((n) => n.kind === "run")
      .map((n) => n.id);
    selectNodes(runIds);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("panel", "data");
      return next;
    }, { replace: true });
  }, [effectiveData, selectNodes, setSearchParams]);

  const handleNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    if (node.type === "detailPill" || node.type === "allDataNode") return;
    const dagNode = node.data?.dagNode as ProvenanceNode | undefined;
    if (!dagNode) return;

    const ids = dagNode.kind === "run"
      ? [dagNode.id]
      : [dagNode.id, ...getDescendantRunIds(dagNode.id, effectiveMaps.parentToChildren, effectiveMaps.nodesById)];

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("nodes", ids.join(","));
      next.delete("edge");
      return next;
    }, { replace: true });

    if (MESH_KINDS.has(dagNode.kind)) {
      setSelectedMeshNode(dagNode);
    } else {
      setSelectedMeshNode(null);
    }
  }, [effectiveMaps, setSearchParams]);

  const handlePaneClick = useCallback(() => {
    clearSelection();
    setSelectedMeshNode(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("edge");
      return next;
    }, { replace: true });
  }, [clearSelection, setSearchParams]);

  const handleCloseMeshPanel = useCallback(() => {
    setSelectedMeshNode(null);
    clearSelection();
  }, [clearSelection]);

  const minimapNodeColor = useCallback((n: Node) => {
    if (n.type === "sweepGroup") return "#16a34a";
    if (n.type === "refQuantityGroup") return "#f97316";
    if (n.type === "allDataNode") return "#2563eb";
    return (n.style?.background as string) ?? "#64748b";
  }, []);

  const handleEditQuantity = useCallback((quantity: ProvenanceNode, geometryId: string | null) => {
    setEditingQuantity({ node: quantity, geometryId });
  }, []);

  const handleAutoDetect = useCallback(async (meshNode: ProvenanceNode, geometryId: string) => {
    const meta = meshNode.metadata ?? {};
    const surfaceMeshId = meta.flow360_surface_mesh_id as string | undefined;
    if (!surfaceMeshId) return;

    setAutoDetectState({ detecting: true, result: null, error: null, geometryId });
    try {
      const res = await detectWingArea(surfaceMeshId, projectId, { includeViz: true });
      setAutoDetectState({ detecting: false, result: res, error: null, geometryId });
      if (res.viz) {
        const v = res.viz;
        usePlanformStore.getState().setData({
          vizData: v,
          liftAxisIdx: v.lift_axis_idx,
          planformAxes: v.planform_axes,
          projZ: v.trapezoid_outline[0]?.[v.lift_axis_idx] ?? 0,
        });
        setOpenPanel("viewer");
      }
    } catch (e) {
      setAutoDetectState({ detecting: false, result: null, error: e instanceof Error ? e.message : String(e), geometryId });
    }
  }, [projectId, setOpenPanel]);

  const handleAllDataClickRef = useRef(handleAllDataClick);
  handleAllDataClickRef.current = handleAllDataClick;

  const handleEditQuantityRef = useRef(handleEditQuantity);
  handleEditQuantityRef.current = handleEditQuantity;

  const handleAutoDetectRef = useRef(handleAutoDetect);
  handleAutoDetectRef.current = handleAutoDetect;

  const handleAutoDetectConfirm = useCallback(async (area: number) => {
    if (!autoDetectState) return;
    const det = autoDetectState.result;
    if (det?.detection_id) {
      try { await confirmWingArea(det.detection_id, area, projectId); } catch { /* best-effort */ }
    }
    const src = "wing_area_detection";
    const lu: string = det?.length_unit ?? "m";
    const areaUnit = `${lu}²`;
    try {
      await setReferenceQuantity(projectId, "ref_area", area, areaUnit, src);
      if (det) {
        const areaParams = new Set(["s_gross", "s_net"]);
        const extras: [string, number][] = [
          ["s_gross", det.s_gross],
          ["s_net", det.s_net],
          ["wing_span", det.wing_span],
          ["root_chord", det.root_chord],
          ["tip_chord", det.tip_chord],
          ["standard_mean_chord", det.standard_mean_chord],
        ];
        await Promise.all(
          extras
            .filter(([, v]) => v != null && v > 0)
            .map(([p, v]) => setReferenceQuantity(projectId, p, v, areaParams.has(p) ? areaUnit : lu, src)),
        );
      }
    } catch { /* best-effort */ }
    setAutoDetectState(null);
  }, [autoDetectState, projectId]);

  const handleNodeDoubleClick = useCallback((_event: React.MouseEvent, node: Node) => {
    if (node.type === "detailPill" || node.type === "sweepGroup" || node.type === "allDataNode") return;
    if (node.type === "refQuantityGroup") {
      const group = node.data?.group as { quantities?: { path?: string }[] } | undefined;
      const firstPath = group?.quantities?.[0]?.path;
      const segments = firstPath ? String(firstPath).split(".") : [];
      const geomLabel = segments.length >= 3 ? segments[1] : undefined;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("panel", "config");
        next.set("configTab", "reference-quantities");
        if (geomLabel) next.set("refGroup", geomLabel);
        else next.delete("refGroup");
        return next;
      }, { replace: true });
      return;
    }
    const dagNode = node.data?.dagNode as ProvenanceNode | undefined;
    if (node.type === "plotNode" && dagNode) {
      selectArtifact(dagNode.id);
      setOpenPanel("saved-plots");
      return;
    }
    if (node.data?.hasDetails) toggleNodeDetails(node.id);
  }, [toggleNodeDetails, selectArtifact, setOpenPanel, setSearchParams]);

  const handleNodeContextMenu = useCallback((event: ReactMouseEvent, node: Node) => {
    event.preventDefault();
    if (node.type === "detailPill") return;
    if (node.type === "sweepGroup") {
      const group = node.data?.group as { groupId?: string; title?: string; runs?: { id: string }[] } | undefined;
      const firstRunId = group?.runs?.[0]?.id;
      if (!firstRunId) return;
      setCtxMenu({ x: event.clientX, y: event.clientY, nodeId: firstRunId, nodeName: group?.title ?? "Sweep Group" });
      return;
    }
    const dagNode = node.data?.dagNode as ProvenanceNode | undefined;
    if (!dagNode) return;
    setCtxMenu({ x: event.clientX, y: event.clientY, nodeId: dagNode.id, nodeName: dagNode.name });
  }, []);

  const flowNodesById = useMemo(() => {
    const m = new Map<string, Node>();
    for (const n of nodes) m.set(n.id, n);
    return m;
  }, [nodes]);

  const handleEdgeContextMenu = useCallback((event: ReactMouseEvent, edge: Edge) => {
    event.preventDefault();
    const dagEdgeId = (edge.data as Record<string, unknown> | undefined)?.dagEdgeId as string | undefined;
    const sourceNode = dataMaps.nodesById.get(edge.source);
    const targetNode = dataMaps.nodesById.get(edge.target);
    const label = `${sourceNode?.name ?? edge.source} → ${targetNode?.name ?? edge.target}`;
    if (dagEdgeId) {
      setCtxMenu({ x: event.clientX, y: event.clientY, edgeId: dagEdgeId, edgeLabel: label });
    } else {
      // Synthetic edge — resolve to best endpoint node for detail.
      const resolveNodeId = (id: string): string | undefined => {
        const n = flowNodesById.get(id);
        return (n?.data?.dagNode as ProvenanceNode | undefined)?.id;
      };
      const nodeId = resolveNodeId(edge.target) ?? resolveNodeId(edge.source);
      setCtxMenu({ x: event.clientX, y: event.clientY, nodeId, nodeName: label });
    }
  }, [dataMaps, flowNodesById]);

  const handleEdgeMouseEnter = useCallback((event: ReactMouseEvent, edge: Edge) => {
    const d = edge.data as Record<string, unknown> | undefined;
    const text = edgeTooltipContent(
      {
        source: edge.source,
        target: edge.target,
        label: edge.label as string | undefined,
        operation: d?.operation as string | undefined,
        metadata: d?.metadata as Record<string, unknown> | undefined,
        created_at: d?.created_at as string | undefined,
      },
      edgeTooltipNodes,
    );
    setEdgeHover({ x: event.clientX, y: event.clientY - 28, text });
  }, [edgeTooltipNodes]);

  const handleEdgeMouseLeave = useCallback(() => setEdgeHover(null), []);

  const handleEdgeClick = useCallback((_event: ReactMouseEvent, edge: Edge) => {
    const dagEdgeId = (edge.data as Record<string, unknown> | undefined)?.dagEdgeId as string | undefined;
    if (dagEdgeId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("edge", dagEdgeId);
        next.delete("nodes");
        next.set("panel", "detail");
        return next;
      }, { replace: true });
      return;
    }
    // Synthetic edge — resolve to the best endpoint node.
    const resolveNodeId = (id: string): string | undefined => {
      const n = flowNodesById.get(id);
      return (n?.data?.dagNode as ProvenanceNode | undefined)?.id;
    };
    const nodeId = resolveNodeId(edge.target) ?? resolveNodeId(edge.source);
    if (nodeId) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("nodes", nodeId);
        next.delete("edge");
        next.set("panel", "detail");
        return next;
      }, { replace: true });
    }
  }, [setSearchParams, flowNodesById]);

  const handleShowDetail = useCallback(() => {
    if (!ctxMenu) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (ctxMenu.nodeId) {
        next.set("nodes", ctxMenu.nodeId);
        next.delete("edge");
      } else if (ctxMenu.edgeId) {
        next.set("edge", ctxMenu.edgeId);
        next.delete("nodes");
      }
      next.set("panel", "detail");
      return next;
    }, { replace: true });
    setCtxMenu(null);
  }, [ctxMenu, setSearchParams]);

  const handleStartConversation = useCallback(() => {
    if (!ctxMenu) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (ctxMenu.nodeId) {
        next.set("nodes", ctxMenu.nodeId);
        next.delete("edge");
      } else if (ctxMenu.edgeId) {
        next.set("edge", ctxMenu.edgeId);
        next.delete("nodes");
      }
      next.set("panel", "conversations");
      return next;
    }, { replace: true });
    setCtxMenu(null);
  }, [ctxMenu, setSearchParams]);

  const handleDeleteNode = useCallback(() => {
    if (!ctxMenu?.nodeId) return;
    const nodeId = ctxMenu.nodeId;
    setCtxMenu(null);
    deleteMutation.mutate(nodeId, { onSuccess: () => clearSelection() });
  }, [ctxMenu, deleteMutation, clearSelection]);

  useEffect(() => {
    if (!ctxMenu) return;
    const dismiss = () => setCtxMenu(null);
    window.addEventListener("click", dismiss);
    window.addEventListener("scroll", dismiss, true);
    return () => {
      window.removeEventListener("click", dismiss);
      window.removeEventListener("scroll", dismiss, true);
    };
  }, [ctxMenu]);

  const sweepGroups = useMemo(() => {
    if (!effectiveData?.nodes.length || !groupingEnabled) return [];
    return detectSweepGroups(effectiveData.nodes, effectiveData.edges);
  }, [effectiveData, groupingEnabled]);

  const refQuantityGroups = useMemo(() => {
    if (!effectiveData?.nodes.length || !groupingEnabled) return [];
    return detectRefQuantityGroups(effectiveData.nodes, effectiveData.edges);
  }, [effectiveData, groupingEnabled]);

  const hasGroups = sweepGroups.length > 0 || refQuantityGroups.length > 0;

  const sweepSizeById = useMemo(() => {
    const m = new Map<string, number>();
    for (const g of sweepGroups) m.set(g.groupId, g.runs.length);
    return m;
  }, [sweepGroups]);

  const toggleGroup = useCallback((groupId: string) => {
    // Refuse to expand groups larger than MAX_EXPANDABLE_SWEEP_SIZE. ELK
    // can't lay out 500+ sibling nodes in a reasonable time, and the user
    // can't meaningfully click between individual runs at that density.
    // Collapsing is always allowed.
    const size = sweepSizeById.get(groupId);
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else if (size !== undefined && size > MAX_EXPANDABLE_SWEEP_SIZE) {
        // Silently ignore the expand. The sweep node renders a disabled
        // state so this branch is only reachable via URL share.
        return prev;
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, [sweepSizeById]);

  const effectiveExpandedDetails = useMemo(() => {
    if (!detailsGloballyVisible) return expandedDetails;
    const all = new Set(expandedDetails);
    for (const n of effectiveData?.nodes ?? []) all.add(n.id);
    return all;
  }, [detailsGloballyVisible, expandedDetails, effectiveData?.nodes]);

  const layoutInFlightRef = useRef(false);
  const layoutPendingRef = useRef(false);

  const runLayout = useCallback(async (stabilizeAfterPaint = true) => {
    if (!effectiveData?.nodes.length) return;
    if (layoutInFlightRef.current) {
      layoutPendingRef.current = true;
      return;
    }
    layoutInFlightRef.current = true;

    const hasDetails = (n: ProvenanceNode) =>
      n.value_json && Object.keys(n.value_json).length > 0;

    const flowNodes: Node[] = effectiveData.nodes.map((n) => {
      const isPlot = n.kind === "saved_plot";
      const cc = commentCountsRef.current?.[n.id] ?? 0;
      return {
        id: n.id,
        type: isPlot ? "plotNode" : "commentBadge",
        data: {
          label: isPlot ? n.name : `${n.kind}: ${n.name}`,
          dagNode: n,
          commentCount: cc,
          hasDetails: hasDetails(n),
          detailsExpanded: effectiveExpandedDetails.has(n.id),
          onToggleDetails: () => toggleNodeDetails(n.id),
        },
        position: { x: 0, y: 0 },
        style: isPlot
          ? undefined
          : {
              background: KIND_COLORS[n.kind] ?? "#64748b",
              color: "#fff",
              borderRadius: 6,
              padding: "6px 12px",
              fontSize: 12,
              border: "none",
              cursor: "pointer",
              boxSizing: "border-box" as const,
              width: COMMENT_BADGE_WIDTH,
              height: COMMENT_BADGE_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              whiteSpace: "nowrap" as const,
            },
      };
    });

    const flowEdges: Edge[] = effectiveData.edges.map((e, i) => ({
      id: e.id || `edge-${i}`,
      source: e.parent_id,
      target: e.child_id,
      label: e.relationship ?? undefined,
      style: { stroke: isLight ? "#94a3b8" : "#64748b", strokeWidth: 1.5 },
      data: {
        dagEdgeId: e.id,
        operation: e.operation,
        metadata: e.metadata,
        created_at: e.created_at,
      },
    }));

    const { nodes: sweepNodes, edges: sweepEdges } = applySweepGroups(
      flowNodes,
      flowEdges,
      sweepGroups,
      expandedGroups,
      toggleGroup,
      (...args) => selectArtifactRef.current(...args),
      MAX_EXPANDABLE_SWEEP_SIZE,
    );

    const sourceGroups = detectSourceGroups(effectiveData.nodes, effectiveData.edges);
    const { nodes: srcGroupedNodes, edges: srcGroupedEdges } = applySourceGroups(
      sweepNodes,
      sweepEdges,
      sourceGroups,
      expandedGroups,
      toggleGroup,
    );

    const { nodes: groupedNodes, edges: groupedEdges } = applyRefQuantityGroups(
      srcGroupedNodes,
      srcGroupedEdges,
      refQuantityGroups,
      {
        onSelectNode: (...args) => selectArtifactRef.current(...args),
        onEditQuantity: isAnonymous ? undefined : (...args: Parameters<typeof handleEditQuantity>) => handleEditQuantityRef.current(...args),
        onAutoDetect: (...args: Parameters<typeof handleAutoDetect>) => handleAutoDetectRef.current(...args),
        onToggleRefGroup: (gid: string) => setExpandedRefGroups((prev) => {
          const next = new Set(prev);
          if (next.has(gid)) next.delete(gid); else next.add(gid);
          return next;
        }),
      },
      expandedRefGroups,
    );

    const runs = effectiveData.nodes.filter((n) => n.kind === "run");
    const hasRuns = runs.length > 0;
    const runCount = hasRuns
      ? runs.length
      : effectiveData.nodes.filter((n) =>
          n.kind === "dataframe" || n.kind === "scalar" || n.kind === "saved_plot",
        ).length;
    const tableSnapshot = runTableDataRef.current;
    const { nodes: allNodes, edges: allEdges } = injectAllDataNode(
      groupedNodes,
      groupedEdges,
      projectId,
      runCount,
      (...args: Parameters<typeof handleAllDataClick>) => handleAllDataClickRef.current(...args),
      tableSnapshot
        ? {
            columns: tableSnapshot.columns,
            rows: tableSnapshot.rows,
            selectedNodeId: selectedArtifactIdRef.current,
            onRowClick: (nodeId: string) => selectArtifactRef.current(nodeId),
          }
        : undefined,
      hasRuns ? "runs" : "artifacts",
    );

    const { nodes: withDetails, edges: withDetailEdges } = injectDetailNodes(
      allNodes,
      allEdges,
      effectiveExpandedDetails,
    );

    try {
      const laid = await layoutDAG(withDetails, withDetailEdges);
      setNodes(laid.nodes);
      setEdges(laid.edges);
      setIsLayoutReady(true);
      layoutIdRef.current += 1;
      if (stabilizeAfterPaint) pendingLayoutStabilizationRef.current = layoutIdRef.current;
      setLayoutId(layoutIdRef.current);
    } finally {
      layoutInFlightRef.current = false;
      if (layoutPendingRef.current) {
        layoutPendingRef.current = false;
        void runLayout(stabilizeAfterPaint);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- callbacks accessed via refs to avoid spurious ELK re-layouts
  }, [effectiveData, isLight, isAnonymous, sweepGroups, refQuantityGroups, expandedGroups, expandedRefGroups, toggleGroup, setNodes, setEdges, effectiveExpandedDetails, toggleNodeDetails, projectId]);

  useEffect(() => {
    void runLayout();
  }, [runLayout]);

  useEffect(() => {
    if (!nodesInitialized || !isLayoutReady) return;
    if (pendingLayoutStabilizationRef.current !== layoutId) return;
    pendingLayoutStabilizationRef.current = null;

    // Only run the stabilisation pass when node membership changed
    // since the last layout — avoids an expensive double ELK pass on
    // every poll when the graph hasn't actually changed.
    if (topologySignature === prevTopologySignatureRef.current) return;
    prevTopologySignatureRef.current = topologySignature;

    const frame = requestAnimationFrame(() => {
      void runLayout(false);
    });
    return () => cancelAnimationFrame(frame);
  }, [nodesInitialized, isLayoutReady, layoutId, runLayout, topologySignature]);

  // Apply selection ring + gold flash + staleness border without triggering a full ELK re-layout.
  const prevSelectedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!isLayoutReady) return;
    const prevSelected = prevSelectedRef.current;
    const newlySelected = new Set([...selectedNodeIds].filter(id => !prevSelected.has(id)));
    prevSelectedRef.current = new Set(selectedNodeIds);

    setNodes((prev) =>
      prev.map((n) => {
        if (n.type === "detailPill") return n;

        if (n.type === "allDataNode") {
          const currentSelectedId = (n.data as Record<string, unknown>)?.selectedNodeId;
          if (currentSelectedId !== selectedArtifactId) {
            return { ...n, data: { ...n.data, selectedNodeId: selectedArtifactId } };
          }
          return n;
        }

        if (n.type === "sweepGroup" || n.type === "refQuantityGroup") return n;

        const dagNode = n.data?.dagNode as ProvenanceNode | undefined;
        if (!dagNode) return n;
        const isSelected = selectedNodeIds.has(dagNode.id);
        const isMeshHighlighted = selectedMeshNode?.id === dagNode.id;
        const stale = !!(dagNode as Record<string, unknown>).is_stale;
        const baseColor = KIND_COLORS[dagNode.kind] ?? "#64748b";
        const bgColor = stale ? desaturateColor(baseColor) : baseColor;
        const shouldFlash = newlySelected.has(dagNode.id);
        return {
          ...n,
          className: [
            n.className?.replace(/\bnode-selected-flash\b/, "").trim() ?? "",
            shouldFlash ? "node-selected-flash" : "",
          ].filter(Boolean).join(" ") || undefined,
          style: {
            ...n.style,
            background: bgColor,
            border: (isSelected || isMeshHighlighted)
              ? "2px solid #fbbf24"
              : stale
              ? "2px solid #f59e0b"
              : "none",
          },
        };
      })
    );
  }, [selectedNodeIds, selectedArtifactId, selectedMeshNode, isLayoutReady, setNodes]);

  // Sync run table data into AllDataNode without triggering a full ELK re-layout.
  useEffect(() => {
    if (!isLayoutReady || !runTableData) return;
    setNodes((prev) =>
      prev.map((n) => {
        if (n.type !== "allDataNode") return n;
        return {
          ...n,
          data: {
            ...n.data,
            columns: runTableData.columns,
            rows: runTableData.rows,
            selectedNodeId: selectedArtifactId,
          },
        };
      }),
    );
  }, [runTableData, selectedArtifactId, isLayoutReady, setNodes]);

  if (isLoading) {
    return <div style={{ padding: 16, color: "var(--gray-9)" }}>Loading DAG...</div>;
  }

  if (isError) {
    return <div style={{ padding: 16, color: "var(--red-9)" }}>Failed to load DAG</div>;
  }

  if (!data?.nodes.length) {
    return <div style={{ padding: 16, color: "var(--gray-9)" }}>No nodes in this project yet.</div>;
  }

  if (!isLayoutReady) {
    return <div style={{ padding: 16, color: "var(--gray-9)" }}>Laying out graph...</div>;
  }

  return (
    <div ref={dagContainerRef} data-tour="dag-panel" style={{ height: "100%", width: "100%" }}>
    <ReactFlow
      nodes={nodes}
      edges={edges}
      viewport={viewport}
      onViewportChange={onViewportChange}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeClick={handleNodeClick}
      onNodeDoubleClick={handleNodeDoubleClick}
      onNodeContextMenu={handleNodeContextMenu}
      onEdgeClick={handleEdgeClick}
      onEdgeContextMenu={handleEdgeContextMenu}
      onEdgeMouseEnter={handleEdgeMouseEnter}
      onEdgeMouseLeave={handleEdgeMouseLeave}
      onPaneClick={handlePaneClick}
      onPaneContextMenu={(e) => {
        paneCtxMenu.handleContextMenu(e as unknown as React.MouseEvent);
      }}
      nodeTypes={NODE_TYPES}
      edgeTypes={EDGE_TYPES}
      colorMode={theme}
      proOptions={PRO_OPTIONS}
      onlyRenderVisibleElements
    >
      <LayoutFitter layoutId={layoutId} />
      <ZoomBridge containerRef={dagContainerRef} />
      <ViewportStabilizer containerRef={dagContainerRef} />
      <DAGViewStateRegistrar
        groupingEnabled={groupingEnabled}
        expandedGroups={expandedGroups}
        expandedDetails={expandedDetails}
        detailsGloballyVisible={detailsGloballyVisible}
        setGroupingEnabled={setGroupingEnabled}
        setExpandedGroups={setExpandedGroups}
        setExpandedDetails={setExpandedDetails}
        setDetailsGloballyVisible={setDetailsGloballyVisible}
      />
      <Background color={isLight ? "#cbd5e1" : "#334155"} gap={16} />
      <Controls />
      {selectedMeshNode && (
        <MeshActionPanel
          node={selectedMeshNode}
          projectId={projectId}
          onClose={handleCloseMeshPanel}
        />
      )}
      {autoDetectState && !autoDetectState.detecting && autoDetectState.result && (
        <div style={{
          position: "absolute",
          top: 10,
          left: 10,
          zIndex: 10,
          background: "var(--color-surface)",
          border: "1px solid var(--gray-a5)",
          borderRadius: 8,
          padding: 16,
          width: 280,
          boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
          fontSize: 13,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Wing Area Detected</div>
          <div>S_gross: <strong>{autoDetectState.result.s_gross?.toFixed(4) ?? "—"}</strong></div>
          <div>S_net: <strong>{autoDetectState.result.s_net?.toFixed(4) ?? "—"}</strong></div>
          <div style={{ fontSize: 11, color: "var(--gray-9)", marginTop: 4, marginBottom: 8 }}>
            {autoDetectState.result.confidence}
            {autoDetectState.result.viz && " — toggle Planform in the 3D viewer to inspect"}
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button
              onClick={() => handleAutoDetectConfirm(autoDetectState.result!.s_gross ?? autoDetectState.result!.projected_area)}
              style={{
                padding: "4px 12px", borderRadius: 4, border: "none",
                background: "var(--green-9)", color: "white", fontSize: 12, cursor: "pointer",
              }}
            >Use as S_ref</button>
            <button
              onClick={() => setAutoDetectState(null)}
              style={{
                padding: "4px 12px", borderRadius: 4, border: "1px solid var(--gray-a6)",
                background: "transparent", color: "var(--gray-11)", fontSize: 12, cursor: "pointer",
              }}
            >Dismiss</button>
          </div>
        </div>
      )}
      {autoDetectState?.detecting && (
        <div style={{
          position: "absolute", top: 10, left: 10, zIndex: 10,
          background: "var(--color-surface)", border: "1px solid var(--gray-a5)",
          borderRadius: 8, padding: 16, fontSize: 13,
          boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
        }}>
          Detecting wing area...
        </div>
      )}
      {autoDetectState?.error && (
        <div style={{
          position: "absolute", top: 10, left: 10, zIndex: 10,
          background: "var(--color-surface)", border: "1px solid var(--gray-a5)",
          borderRadius: 8, padding: 16, fontSize: 13,
          boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
        }}>
          <div style={{ color: "var(--red-9)", marginBottom: 8 }}>Detection failed: {autoDetectState.error}</div>
          <button
            onClick={() => setAutoDetectState(null)}
            style={{
              padding: "4px 12px", borderRadius: 4, border: "1px solid var(--gray-a6)",
              background: "transparent", color: "var(--gray-11)", fontSize: 12, cursor: "pointer",
            }}
          >Dismiss</button>
        </div>
      )}
      <MiniMap
        nodeColor={minimapNodeColor}
        maskColor={isLight ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)"}
        pannable
        zoomable
      />

      <Panel position="top-right">
        <div data-tour="dag-controls" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Button
            size="1"
            variant="soft"
            onClick={() => void runLayout()}
            title="Re-run automatic layout"
          >
            <span style={{ fontSize: 12 }}>⟳</span>
            Auto-arrange
          </Button>
          {hasActiveFilter && (
            <Button
              size="1"
              variant={showFilteredDAG ? "solid" : "soft"}
              onClick={() => setShowFilteredDAG((v) => !v)}
              title={showFilteredDAG ? "Show full DAG (all nodes)" : "Show only nodes matching the current filter"}
            >
              <span style={{ fontSize: 12 }}>{showFilteredDAG ? "⧫" : "◇"}</span>
              {showFilteredDAG
                ? `Filtered (${filteredData.length}/${fullData.length})`
                : "Show Filtered"}
            </Button>
          )}
          {hasGroups && (
            <Button
              size="1"
              variant="soft"
              onClick={() => setGroupingEnabled((v) => !v)}
              title={groupingEnabled ? "Show all individual runs" : "Group sweep runs"}
            >
              <span style={{ fontSize: 12 }}>{groupingEnabled ? "▤" : "▦"}</span>
              {groupingEnabled ? "Ungroup Sweeps" : "Group Sweeps"}
            </Button>
          )}
          <Button
            size="1"
            variant={detailsGloballyVisible ? "solid" : "soft"}
            onClick={() => {
              setDetailsGloballyVisible((v) => !v);
              if (detailsGloballyVisible) setExpandedDetails(new Set());
            }}
            title={detailsGloballyVisible ? "Hide quantity details" : "Show quantity details on all nodes (double-click a node to toggle individually)"}
          >
            <span style={{ fontSize: 12 }}>{detailsGloballyVisible ? "◉" : "○"}</span>
            {detailsGloballyVisible ? "Hide Details" : "Show Details"}
          </Button>
          {fullData.length > 0 && (
            <ProvenancePopover data={fullData} theme={theme} />
          )}
        </div>
      </Panel>
    </ReactFlow>

    {paneCtxMenu.menu && <DataPointContextMenu menu={paneCtxMenu.menu} onClose={paneCtxMenu.close} />}

    {ctxMenu && (
      <div
        style={{
          position: "fixed",
          top: ctxMenu.y,
          left: ctxMenu.x,
          zIndex: 1000,
          background: "var(--color-surface)",
          border: "1px solid var(--gray-a5)",
          borderRadius: 8,
          boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
          minWidth: 180,
          padding: 4,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "4px 8px", borderBottom: "1px solid var(--gray-a4)", marginBottom: 4 }}>
          <span style={{ fontSize: 11, color: "var(--gray-9)", fontWeight: 500 }}>
            {ctxMenu.nodeName ?? ctxMenu.edgeLabel ?? "Edge"}
          </span>
        </div>
        <button
          onClick={handleShowDetail}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            width: "100%",
            padding: "6px 8px",
            fontSize: 12,
            color: "var(--gray-12)",
            background: "transparent",
            border: "none",
            borderRadius: 4,
            cursor: "pointer",
            fontFamily: "inherit",
            textAlign: "left",
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--accent-a3)"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
        >
          <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
            <path d="M7.5 1.5a6 6 0 1 0 0 12 6 6 0 0 0 0-12ZM7.5 5v0m0 2v3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          Show detail
        </button>
        <button
          onClick={handleStartConversation}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            width: "100%",
            padding: "6px 8px",
            fontSize: 12,
            color: "var(--gray-12)",
            background: "transparent",
            border: "none",
            borderRadius: 4,
            cursor: "pointer",
            fontFamily: "inherit",
            textAlign: "left",
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--accent-a3)"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
        >
          <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
            <path d="M12.5 3h-10a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1H5l2.5 2.5L10 11h2.5a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1Z" stroke="currentColor" strokeWidth="1.2" />
          </svg>
          Start a conversation
        </button>
        {ctxMenu.nodeId && !isAnonymous && (
          <button
            onClick={handleDeleteNode}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              width: "100%",
              padding: "6px 8px",
              fontSize: 12,
              color: "var(--red-9)",
              background: "transparent",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              fontFamily: "inherit",
              textAlign: "left",
            }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--red-a3)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
          >
            <svg width="14" height="14" viewBox="0 0 15 15" fill="none">
              <path d="M5.5 1h4M2 3.5h11M12 3.5l-.5 8.5a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 12L3 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Delete
          </button>
        )}
      </div>
    )}

    {edgeHover && (
      <div
        style={{
          position: "fixed",
          top: edgeHover.y,
          left: edgeHover.x,
          zIndex: 999,
          background: "var(--color-surface)",
          border: "1px solid var(--gray-a5)",
          borderRadius: 6,
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          padding: "4px 8px",
          fontSize: 11,
          color: "var(--gray-12)",
          whiteSpace: "pre-line",
          pointerEvents: "none",
          transform: "translateX(-50%)",
          maxWidth: 300,
        }}
      >
        {edgeHover.text}
      </div>
    )}

    {editingQuantity && (
      <RefQuantityEditModal
        open={!!editingQuantity}
        onClose={() => setEditingQuantity(null)}
        projectId={projectId}
        branch={currentBranch}
        quantity={editingQuantity.node}
        geometryId={editingQuantity.geometryId}
      />
    )}
    </div>
  );
}
